import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

/* M1 · Create ABHA via Aadhaar OTP — 3-step wizard.
 * Step 1 → Aadhaar + OTP
 * Step 2 → (optional) Communication mobile OTP
 * Step 3 → ABHA created; link to a patient.
 */
export default function CreateABHAWizard({ patients = [], onLinked }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [aadhaar, setAadhaar] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [aadhaarTxn, setAadhaarTxn] = useState("");
  const [mobileTxn, setMobileTxn] = useState("");
  const [profile, setProfile] = useState(null);
  const [xToken, setXToken] = useState("");
  const [needsMobileVerify, setNeedsMobileVerify] = useState(false);
  const [linkPatientId, setLinkPatientId] = useState("");
  const [cardB64, setCardB64] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [chosenAddress, setChosenAddress] = useState("");
  const [customAddress, setCustomAddress] = useState("");

  const reset = () => {
    setStep(1); setBusy(false); setAadhaar(""); setMobile(""); setOtp("");
    setAadhaarTxn(""); setMobileTxn(""); setProfile(null); setXToken("");
    setNeedsMobileVerify(false); setLinkPatientId(""); setCardB64("");
    setSuggestions([]); setChosenAddress(""); setCustomAddress("");
  };

  const errText = (r) => r?.response?.error?.message || r?.response?.message || r?.error || r?.message || `HTTP ${r?.http_status || 0}`;

  const sendAadhaarOTP = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/abha/enrol/request-aadhaar-otp", { aadhaar });
      if (!data.ok) return toast.error(errText(data));
      setAadhaarTxn(data.response?.txnId || "");
      toast.success("OTP sent to Aadhaar-linked mobile");
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const verifyAadhaarAndCreate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/abha/enrol/by-aadhaar", { txn_id: aadhaarTxn, otp, mobile });
      if (!data.ok) return toast.error(errText(data));
      const resp = data.response || {};
      const prof = resp.ABHAProfile || resp.abhaProfile || resp.profile || {};
      setProfile(prof);
      setXToken(resp.tokens?.token || resp.token || "");
      const mobileVerified = resp.mobileVerified === true || resp.isMobileVerified === true;
      if (mobile && !mobileVerified) {
        setNeedsMobileVerify(true);
        toast.success("ABHA created — verify communication mobile next");
        setStep(3);
      } else {
        toast.success("ABHA created — pick your ABHA address");
        fetchSuggestions();
        setStep(4);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const sendMobileOTP = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/abha/enrol/request-mobile-otp", { txn_id: aadhaarTxn, mobile });
      if (!data.ok) return toast.error(errText(data));
      setMobileTxn(data.response?.txnId || aadhaarTxn);
      setOtp("");
      toast.success("OTP sent to communication mobile");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const verifyMobileOTP = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/abha/enrol/verify-mobile-otp", { txn_id: mobileTxn || aadhaarTxn, otp });
      if (!data.ok) return toast.error(errText(data));
      toast.success("Communication mobile verified");
      // move to address-selection step
      fetchSuggestions();
      setStep(4);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const fetchSuggestions = async () => {
    try {
      const { data } = await api.post("/abdm/abha/enrol/suggest-address", { txn_id: aadhaarTxn });
      if (data.ok) {
        const list = data.response?.abhaAddressList || data.response?.suggestions || [];
        setSuggestions(Array.isArray(list) ? list : []);
      }
    } catch { /* non-fatal — user can type custom */ }
  };

  const setAddress = async () => {
    const addr = (customAddress || chosenAddress || "").trim().replace(/@.*$/, "");
    if (!addr) return toast.error("Choose or enter an ABHA address");
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/abha/enrol/set-address", { txn_id: aadhaarTxn, abha_address: addr, preferred: 1 });
      if (!data.ok) return toast.error(errText(data));
      const resp = data.response || {};
      // Some responses include the finalised ABHA address here
      setProfile((p) => ({ ...(p || {}), preferredAbhaAddress: resp.abhaAddress || addr, healthIdNumber: p?.ABHANumber || p?.abhaNumber }));
      toast.success(`ABHA address ${resp.abhaAddress || addr} set`);
      setStep(5);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const fetchCard = async () => {
    if (!xToken) return toast.error("No T-Token — profile may be incomplete");
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/abha/card", { x_token: xToken });
      if (!data.ok) return toast.error(errText(data));
      setCardB64(data.response?.base64 || "");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const linkToPatient = async () => {
    if (!linkPatientId) return toast.error("Choose a patient");
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/abha/link-to-patient", { patient_id: linkPatientId, abha_profile: profile, x_token: xToken });
      if (!data.ok) return toast.error("Link failed");
      toast.success("ABHA linked to patient");
      onLinked && onLinked();
      reset();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border p-4 space-y-3" data-testid="abdm-create-abha">
      <div className="flex items-center justify-between">
        <div className="label-tiny">M1 · Create ABHA via Aadhaar OTP</div>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <div key={n} className={`w-6 h-6 text-[11px] flex items-center justify-center border rounded-full ${step >= n ? "bg-blue-700 text-white border-blue-700" : "text-slate-500"}`}>{n}</div>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-blue-800 bg-blue-50 border border-blue-200 p-2 rounded-sm">
        Uses official NHA endpoints <code>/enrollment/request/otp</code>, <code>/enrol/byAadhaar</code>, <code>/enrollment/auth/byAbdm</code>. Aadhaar &amp; OTP are RSA-encrypted before transmission per ABDM v3 spec.
      </p>

      {/* Step 1 — Aadhaar */}
      {step === 1 && (
        <div className="space-y-3">
          <div><Label className="label-tiny">Aadhaar Number (12 digits)</Label>
            <Input data-testid="abha-aadhaar-input" value={aadhaar} onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, "").slice(0, 12))} placeholder="XXXX XXXX XXXX" maxLength={12}/>
          </div>
          <div><Label className="label-tiny">Communication Mobile (optional)</Label>
            <Input data-testid="abha-mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile" maxLength={10}/>
            <div className="text-[10px] text-slate-500 mt-1">If same as Aadhaar-linked mobile, skip. If different, we'll verify it separately.</div>
          </div>
          <Button data-testid="abha-send-aad-otp" onClick={sendAadhaarOTP} disabled={busy || aadhaar.length !== 12} className="bg-blue-700 hover:bg-blue-800 rounded-sm">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : null} Send Aadhaar OTP
          </Button>
        </div>
      )}

      {/* Step 2 — OTP → create */}
      {step === 2 && (
        <div className="space-y-3">
          <div className="text-[11px] text-teal-800">OTP dispatched · txn <span className="font-mono">{aadhaarTxn.slice(0, 12)}…</span></div>
          <div><Label className="label-tiny">Aadhaar OTP (6-digit)</Label>
            <Input data-testid="abha-aad-otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6}/>
          </div>
          <div className="flex gap-2">
            <Button data-testid="abha-create" onClick={verifyAadhaarAndCreate} disabled={busy || otp.length !== 6} className="bg-blue-700 hover:bg-blue-800 rounded-sm">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : null} Verify OTP &amp; Create ABHA
            </Button>
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
          </div>
        </div>
      )}

      {/* Step 3 — Communication mobile verification */}
      {step === 3 && needsMobileVerify && (
        <div className="space-y-3">
          <div className="text-[11px] text-slate-600">Verifying communication mobile <span className="font-mono">{mobile}</span> via ABDM OTP.</div>
          {!mobileTxn && (
            <Button data-testid="abha-send-mobile-otp" onClick={sendMobileOTP} disabled={busy} className="bg-blue-700 hover:bg-blue-800 rounded-sm">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : null} Send OTP to {mobile}
            </Button>
          )}
          {mobileTxn && (
            <>
              <div><Label className="label-tiny">Mobile OTP</Label>
                <Input data-testid="abha-mob-otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6}/>
              </div>
              <div className="flex gap-2">
                <Button data-testid="abha-verify-mobile" onClick={verifyMobileOTP} disabled={busy || otp.length !== 6} className="bg-blue-700 hover:bg-blue-800 rounded-sm">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : null} Verify Mobile
                </Button>
                <Button variant="outline" onClick={() => { fetchSuggestions(); setStep(4); }}>Skip</Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 4 — Pick ABHA address */}
      {step === 4 && (
        <div className="space-y-3">
          <div className="border-t pt-3 flex items-center gap-2 text-teal-800"><CheckCircle2 className="w-5 h-5"/> ABHA created — pick a permanent ABHA address</div>
          <Label className="label-tiny">Suggested addresses <span className="text-slate-400">(you can also type your own)</span></Label>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.length === 0 && <div className="text-[11px] text-slate-500">No suggestions returned — type a custom address below.</div>}
            {suggestions.map((s) => (
              <button key={s} type="button" onClick={() => { setChosenAddress(s); setCustomAddress(""); }}
                data-testid={`abha-suggest-${s}`}
                className={`px-2.5 py-1 text-[11px] border rounded-sm ${chosenAddress === s ? "border-blue-700 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300"}`}>
                {s}
              </button>
            ))}
          </div>
          <div>
            <Label className="label-tiny">Or type a custom prefix</Label>
            <div className="flex items-center gap-2">
              <Input data-testid="abha-custom-address" value={customAddress}
                onChange={(e) => { setCustomAddress(e.target.value.replace(/[^a-zA-Z0-9._]/g, "").toLowerCase()); setChosenAddress(""); }}
                placeholder="e.g. rakesh.patel92" className="h-9"/>
              <span className="text-xs text-slate-500 font-mono">@sbx</span>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">Only lowercase letters, digits, dot and underscore. NHA appends the @suffix automatically.</div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="abha-set-address" onClick={setAddress} disabled={busy || (!chosenAddress && !customAddress)} className="bg-blue-700 hover:bg-blue-800 rounded-sm">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : null} Confirm ABHA Address
            </Button>
            <Button variant="outline" onClick={fetchSuggestions} disabled={busy}>Refresh suggestions</Button>
          </div>
        </div>
      )}

      {/* Step 5 — Confirmation & link */}
      {step === 5 && profile && (
        <div className="space-y-3">
          <div className="border-t pt-3 flex items-center gap-2 text-teal-800"><CheckCircle2 className="w-5 h-5"/> ABHA created & finalised</div>
          <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 border p-3">
            <div><span className="text-slate-500">ABHA Number:</span> <span className="font-mono">{profile.ABHANumber || profile.abhaNumber || profile.healthIdNumber || "-"}</span></div>
            <div><span className="text-slate-500">ABHA Address:</span> <span className="font-mono">{profile.preferredAbhaAddress || profile.phrAddress || "-"}</span></div>
            <div><span className="text-slate-500">Name:</span> {[profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ") || profile.name || "-"}</div>
            <div><span className="text-slate-500">DOB:</span> {profile.dayOfBirth || profile.dob ? `${profile.dayOfBirth || ""}/${profile.monthOfBirth || ""}/${profile.yearOfBirth || profile.dob || ""}` : "-"}</div>
            <div><span className="text-slate-500">Gender:</span> {profile.gender || "-"}</div>
            <div><span className="text-slate-500">Mobile:</span> {profile.mobile || mobile || "-"}</div>
          </div>
          {cardB64 && (
            <img alt="ABHA card" src={`data:image/png;base64,${cardB64}`} className="max-h-48 border" data-testid="abha-card-img"/>
          )}
          {!cardB64 && xToken && (
            <Button variant="outline" onClick={fetchCard} disabled={busy} data-testid="abha-fetch-card">Fetch ABHA Card</Button>
          )}
          <div className="border-t pt-3">
            <Label className="label-tiny">Link this ABHA to an existing patient</Label>
            <select value={linkPatientId} onChange={(e) => setLinkPatientId(e.target.value)} className="w-full h-9 border px-2 text-sm" data-testid="abha-link-select">
              <option value="">-- Select patient --</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.uhid}</option>)}
            </select>
            <div className="flex gap-2 mt-2">
              <Button data-testid="abha-link-btn" onClick={linkToPatient} disabled={busy || !linkPatientId} className="bg-teal-700 hover:bg-teal-800 rounded-sm">Link to Patient</Button>
              <Button variant="outline" onClick={reset}>New</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
